// Home/landing-only subset of `helpers/media.ts`. Lives in its own module so
// the marketing page doesn't transitively pull in `MediaEditorStock`,
// `helpers/crop`, etc., which webpack groups with the editor bundle (chunk
// 4435, ~2.3 MB resource). `MediaSelector`, `WEB_ASSET_DEMO_MEDIA` and
// `WEB_ASSET_SHORT_URLS` themselves have zero editor dependencies, so they
// belong in a standalone file.
type SeedString = string;

export const WEB_ASSET_SHORT_URLS = [
  "https://assets.klap.app/web-assets/casey-compressed.mp4",
  "https://assets.klap.app/web-assets/bobby-compressed.mp4",
  "https://assets.klap.app/web-assets/fred-compressed.mp4",
  "https://assets.klap.app/web-assets/unbox-compressed.mp4",
  "https://assets.klap.app/web-assets/beast-compressed.mp4",
  "https://assets.klap.app/web-assets/tom-compressed.mp4",
  "https://assets.klap.app/web-assets/sara-compressed.mp4",
  "https://assets.klap.app/web-assets/billy-compressed.mp4",
];

type WebAssetDemoMediaKey =
  | "publish"
  | "aiGenerate"
  | "demo"
  | "schedule"
  | "shorts"
  | "upload"
  | "framing"
  | "captions"
  | "style"
  | "beforeAfter";

export const WEB_ASSET_DEMO_MEDIA: Record<
  WebAssetDemoMediaKey,
  {
    type: "video" | "image";
    src: string;
    alt: string;
  }
> = {
  publish: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-publish.mp4",
    alt: "Klap publish demo",
  },
  aiGenerate: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-ai-generate.mp4",
    alt: "Klap AI generate demo",
  },
  demo: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-demo.mp4",
    alt: "Klap demo",
  },
  schedule: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-demo-schedule.mp4",
    alt: "Klap schedule demo",
  },
  shorts: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-demo-shorts.mp4",
    alt: "Klap shorts demo",
  },
  upload: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-upload.mp4",
    alt: "Klap upload demo",
  },
  framing: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-framing.mp4",
    alt: "Klap framing demo",
  },
  captions: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-captions.mp4",
    alt: "Klap captions demo",
  },
  style: {
    type: "video",
    src: "https://assets.klap.app/web-assets/landing/klap-style.mp4",
    alt: "Klap style demo",
  },
  beforeAfter: {
    type: "video",
    src: "https://assets.klap.app/web-assets/ai-reframe-compressed.mp4",
    alt: "Klap before/after demo",
  },
};

export class MediaSelector {
  private urls: string[];
  private currentIndex: number = 0;
  private seed: number;

  constructor(urls: string[], seedString: SeedString) {
    this.urls = [...urls]; // Copy to avoid modifying the original array
    this.seed = this.stringToSeed(seedString);
    this.shuffle();
  }

  private stringToSeed(seedString: SeedString): number {
    let hash = 0;
    for (let i = 0; i < seedString.length; i++) {
      const char = seedString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  private random(): number {
    const x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }

  private shuffle(): void {
    for (let i = this.urls.length - 1; i > 0; i--) {
      const j = Math.floor(this.random() * (i + 1));
      [this.urls[i], this.urls[j]] = [this.urls[j], this.urls[i]];
    }
  }

  public getNextUrl(): string {
    if (this.currentIndex >= this.urls.length) {
      this.currentIndex = 0; // Reset to start when all urls are used
      this.shuffle(); // Optionally reshuffle for the next cycle
    }

    return this.urls[this.currentIndex++];
  }
}
